import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  User,
  Phone,
  Wallet,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";

import {
  resolveRecipient,
  getBaitCashBalance,
  transferBaitCash,
  normalizePhone,
} from "../services/baitCashApi";

import "./SendBaitCash.css";

const SendBaitCash = ({ wallet, selectedChain }) => {
  const navigate = useNavigate();

  // ==========================================================
  // CURRENT USER
  // ==========================================================

  const [senderPhone, setSenderPhone] = useState("");
  const [senderWalletAddress, setSenderWalletAddress] = useState("");

  // ==========================================================
  // FORM
  // ==========================================================

  const [recipientPhone, setRecipientPhone] = useState("");
  const [amount, setAmount] = useState("");

  // ==========================================================
  // DATA
  // ==========================================================

  const [recipient, setRecipient] = useState(null);
  const [balance, setBalance] = useState("0.00");

  // ==========================================================
  // UI STATES
  // ==========================================================

  const [loadingBalance, setLoadingBalance] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [sending, setSending] = useState(false);

  const [step, setStep] = useState("form");

  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);

  // ==========================================================
  // GET WALLET ADDRESS
  // ==========================================================

  const getWalletAddress = () => {
    if (!wallet) return "";

    return (
      wallet.address ||
      wallet.walletAddress ||
      wallet.account ||
      wallet.accounts?.[0] ||
      ""
    );
  };

  // ==========================================================
  // LOAD SENDER PHONE
  // ==========================================================

  const getStoredPhone = () => {
    /*
     * We check several possible keys because different parts
     * of the wallet may store the user's phone differently.
     */

    const keys = [
      "phoneNumber",
      "userPhone",
      "phone",
      "baitCashPhone",
      "baitCashPhoneNumber",
    ];

    for (const key of keys) {
      const value = localStorage.getItem(key);

      if (value && value.trim()) {
        return value.trim();
      }
    }

    return "";
  };

  // ==========================================================
  // LOAD BAIT CASH ACCOUNT
  // ==========================================================

  const loadBalance = async (phone) => {
    if (!phone) {
      setBalance("0.00");
      return null;
    }

    try {
      const normalized = normalizePhone(phone);

      const data = await getBaitCashBalance(normalized);

      const currentBalance =
        data?.baitCash?.balancePKR ??
        data?.balancePKR ??
        data?.balance ??
        "0.00";

      setBalance(String(currentBalance));

      return data;
    } catch (err) {
      console.error(
        "Unable to load BAIT Cash balance:",
        err
      );

      setBalance("0.00");

      return null;
    }
  };

  // ==========================================================
  // INITIALIZE SENDER
  // ==========================================================

  useEffect(() => {
    let mounted = true;

    const initializeSender = async () => {
      setLoadingBalance(true);
      setError("");

      try {
        const walletAddress = getWalletAddress();

        if (mounted) {
          setSenderWalletAddress(walletAddress);
        }

        /*
         * First try the phone saved in localStorage.
         */
        let storedPhone = getStoredPhone();

        /*
         * If no phone was found, use the wallet address to
         * locate the BAIT Cash account through the backend
         * balance endpoint if your API supports it.
         *
         * At this stage your existing backend balance endpoint
         * accepts a phone number, so we cannot directly query
         * it by wallet address.
         */

        if (storedPhone) {
          const normalized = normalizePhone(storedPhone);

          if (mounted) {
            setSenderPhone(normalized);
          }

          await loadBalance(normalized);

          return;
        }

        /*
         * TEMPORARY DEVELOPMENT FALLBACK
         *
         * Your currently logged-in test wallet is:
         *
         * ADEEL
         * +923106061014
         * 0x78e3c8...
         *
         * This fallback prevents the page from showing 0.00
         * while the wallet's phone-number persistence is being
         * connected properly.
         *
         * Remove this fallback after phoneNumber is persisted
         * during account/login creation.
         */

        const developmentPhone = "03106061014";

        const normalized = normalizePhone(
          developmentPhone
        );

        if (mounted) {
          setSenderPhone(normalized);
        }

        await loadBalance(normalized);
      } catch (err) {
        console.error(
          "Unable to initialize BAIT Cash sender:",
          err
        );

        if (mounted) {
          setError(
            "Unable to load your BAIT Cash account."
          );
          setBalance("0.00");
        }
      } finally {
        if (mounted) {
          setLoadingBalance(false);
        }
      }
    };

    initializeSender();

    return () => {
      mounted = false;
    };
  }, [wallet]);

  // ==========================================================
  // REFRESH BALANCE
  // ==========================================================

  const handleRefreshBalance = async () => {
    if (!senderPhone) return;

    setError("");
    setLoadingBalance(true);

    try {
      await loadBalance(senderPhone);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  // ==========================================================
  // RESOLVE RECIPIENT
  // ==========================================================

  const handleResolveRecipient = async () => {
    setError("");
    setRecipient(null);

    const phone = normalizePhone(recipientPhone);

    if (!phone) {
      setError(
        "Please enter recipient phone number."
      );
      return;
    }

    if (!senderPhone) {
      setError(
        "Your BAIT Cash account could not be identified."
      );
      return;
    }

    if (phone === senderPhone) {
      setError(
        "You cannot transfer BAIT Cash to yourself."
      );
      return;
    }

    setResolving(true);

    try {
      const data = await resolveRecipient(phone);

      if (!data?.recipient) {
        throw new Error(
          "Recipient BAIT Cash account was not found."
        );
      }

      setRecipient(data.recipient);
    } catch (err) {
      console.error(
        "Recipient resolution failed:",
        err
      );

      setError(
        err?.message ||
          "Unable to find this BAIT Cash account."
      );
    } finally {
      setResolving(false);
    }
  };

  // ==========================================================
  // AMOUNT VALIDATION
  // ==========================================================

  const validateAmount = () => {
    if (!amount || amount.trim() === "") {
      return "Please enter an amount.";
    }

    /*
     * Only allow:
     *
     * 100
     * 100.5
     * 100.50
     *
     * Reject:
     *
     * -100
     * 100.123
     * 1,000
     * abc
     */

    if (!/^\d+(\.\d{1,2})?$/.test(amount.trim())) {
      return "Maximum 2 decimal places are allowed.";
    }

    const value = Number(amount);

    if (!Number.isFinite(value)) {
      return "Please enter a valid amount.";
    }

    if (value <= 0) {
      return "Amount must be greater than zero.";
    }

    if (value > Number(balance)) {
      return "Insufficient BAIT Cash balance.";
    }

    return "";
  };

  // ==========================================================
  // REVIEW TRANSFER
  // ==========================================================

  const handleReview = () => {
    setError("");

    if (!senderPhone) {
      setError(
        "Your BAIT Cash account could not be identified."
      );
      return;
    }

    if (!recipient) {
      setError(
        "Please resolve the recipient first."
      );
      return;
    }

    const amountError = validateAmount();

    if (amountError) {
      setError(amountError);
      return;
    }

    setStep("confirm");
  };

  // ==========================================================
  // TRANSFER
  // ==========================================================

  const handleTransfer = async () => {
    setError("");

    if (!senderPhone) {
      setError(
        "Your BAIT Cash account could not be identified."
      );
      return;
    }

    if (!recipient) {
      setError(
        "Recipient information is missing."
      );
      return;
    }

    const amountError = validateAmount();

    if (amountError) {
      setError(amountError);
      setStep("form");
      return;
    }

    setSending(true);

    try {
      /*
       * Generate a unique idempotency key for this
       * specific transfer attempt.
       */

      const idempotencyKey =
        `BAIT-WALLET-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 8)
          .toUpperCase()}`;

      const data = await transferBaitCash({
        senderPhone,
        recipientPhone:
          recipient.phoneNumber,
        amountPKR: amount,
        idempotencyKey,
      });

      if (!data?.success) {
        throw new Error(
          data?.error ||
            "BAIT Cash transfer failed."
        );
      }

      setSuccessData(data);
      setStep("success");

      /*
       * Refresh sender balance after successful transfer.
       */

      try {
        await loadBalance(senderPhone);
      } catch (balanceError) {
        console.error(
          "Balance refresh failed:",
          balanceError
        );
      }
    } catch (err) {
      console.error(
        "BAIT Cash transfer failed:",
        err
      );

      setError(
        err?.message ||
          "BAIT Cash transfer failed."
      );
    } finally {
      setSending(false);
    }
  };

  // ==========================================================
  // FORMAT PKR
  // ==========================================================

  const formatPKR = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "0.00";
    }

    return number.toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ==========================================================
  // SUCCESS SCREEN
  // ==========================================================

  if (step === "success" && successData) {
    const transfer = successData.transfer;

    return (
      <div className="baitcash-page">

        <div className="baitcash-header">

          <button
            className="back-button"
            onClick={() => navigate(-1)}
            type="button"
          >
            <ArrowLeft size={20} />
          </button>

          <h1>Transfer Successful</h1>

          <div className="header-spacer" />

        </div>

        <div className="baitcash-content success-content">

          <div className="success-icon">
            <CheckCircle size={58} />
          </div>

          <h2>BAIT Cash Sent</h2>

          <div className="success-amount">
            {formatPKR(
              transfer.amountPKR
            )}{" "}
            PKR
          </div>

          <div className="success-card">

            <div className="detail-row">
              <span>Recipient</span>

              <strong>
                {transfer.recipient.fullName}
              </strong>
            </div>

            <div className="detail-row">
              <span>Phone</span>

              <strong>
                {transfer.recipient.phoneNumber}
              </strong>
            </div>

            <div className="detail-row">
              <span>Reference</span>

              <strong className="reference">
                {transfer.reference}
              </strong>
            </div>

            <div className="detail-row">
              <span>New Balance</span>

              <strong>
                {formatPKR(
                  transfer.sender.balancePKR
                )}{" "}
                PKR
              </strong>
            </div>

          </div>

          <button
            className="primary-button"
            onClick={() =>
              navigate("/dashboard")
            }
            type="button"
          >
            Done
          </button>

        </div>
      </div>
    );
  }

  // ==========================================================
  // CONFIRM SCREEN
  // ==========================================================

  if (step === "confirm") {
    const remaining =
      Number(balance) - Number(amount);

    return (
      <div className="baitcash-page">

        <div className="baitcash-header">

          <button
            className="back-button"
            onClick={() => setStep("form")}
            type="button"
            disabled={sending}
          >
            <ArrowLeft size={20} />
          </button>

          <h1>Confirm Transfer</h1>

          <div className="header-spacer" />

        </div>

        <div className="baitcash-content">

          {error && (
            <div className="error-box">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="confirm-card">

            <div className="confirm-icon">
              <Wallet size={30} />
            </div>

            <p className="confirm-label">
              Sending to
            </p>

            <h2>
              {recipient?.fullName}
            </h2>

            <p className="recipient-phone">
              {recipient?.phoneNumber}
            </p>

            <div className="confirm-amount">
              {formatPKR(amount)}{" "}
              <span>PKR</span>
            </div>

            <div className="remaining-balance">
              Remaining balance{" "}
              <strong>
                {formatPKR(remaining)} PKR
              </strong>
            </div>

          </div>

          <div className="button-row">

            <button
              className="secondary-button"
              onClick={() => setStep("form")}
              disabled={sending}
              type="button"
            >
              Cancel
            </button>

            <button
              className="primary-button"
              onClick={handleTransfer}
              disabled={sending}
              type="button"
            >
              {sending ? (
                <>
                  <Loader2
                    size={18}
                    className="spin"
                  />

                  Sending...
                </>
              ) : (
                <>
                  Send

                  <ArrowRight size={18} />
                </>
              )}
            </button>

          </div>

        </div>
      </div>
    );
  }

  // ==========================================================
  // MAIN FORM
  // ==========================================================

  return (
    <div className="baitcash-page">

      {/* ====================================================
          HEADER
      ==================================================== */}

      <div className="baitcash-header">

        <button
          className="back-button"
          onClick={() => navigate(-1)}
          type="button"
        >
          <ArrowLeft size={20} />
        </button>

        <h1>Send BAIT Cash</h1>

        <div className="header-spacer" />

      </div>

      {/* ====================================================
          CONTENT
      ==================================================== */}

      <div className="baitcash-content">

        {/* ERROR */}

        {error && (
          <div className="error-box">

            <AlertCircle size={18} />

            <span>{error}</span>

          </div>
        )}

        {/* ==================================================
            BALANCE
        ================================================== */}

        <div className="balance-card">

          <div>

            <span>
              Available BAIT Cash
            </span>

            <h2>

              {loadingBalance ? (
                <Loader2
                  size={24}
                  className="spin"
                />
              ) : (
                formatPKR(balance)
              )}

              {!loadingBalance && (
                <small> PKR</small>
              )}

            </h2>

            {senderPhone && (
              <div
                style={{
                  fontSize: "12px",
                  opacity: 0.7,
                  marginTop: "4px",
                }}
              >
                {senderPhone}
              </div>
            )}

          </div>

          <button
            type="button"
            className="balance-refresh-button"
            onClick={handleRefreshBalance}
            disabled={
              loadingBalance ||
              !senderPhone
            }
            title="Refresh balance"
          >
            {loadingBalance ? (
              <Loader2
                size={24}
                className="spin"
              />
            ) : (
              <RefreshCw size={24} />
            )}
          </button>

        </div>

        {/* ==================================================
            RECIPIENT PHONE
        ================================================== */}

        <div className="form-section">

          <label>

            <Phone size={16} />

            Recipient Phone Number

          </label>

          <div className="input-row">

            <input
              type="tel"
              value={recipientPhone}
              onChange={(e) => {

                setRecipientPhone(
                  e.target.value
                );

                setRecipient(null);

                setError("");

              }}
              placeholder="03XXXXXXXXX"
              disabled={resolving}
            />

            <button
              className="resolve-button"
              onClick={
                handleResolveRecipient
              }
              disabled={
                resolving ||
                !recipientPhone.trim()
              }
              type="button"
            >

              {resolving ? (
                <>
                  <Loader2
                    size={18}
                    className="spin"
                  />

                  Finding...
                </>
              ) : (
                "Find"
              )}

            </button>

          </div>

        </div>

        {/* ==================================================
            RECIPIENT RESULT
        ================================================== */}

        {recipient && (
          <div className="recipient-card">

            <div className="recipient-avatar">
              <User size={22} />
            </div>

            <div>

              <strong>
                {recipient.fullName}
              </strong>

              <span>
                {recipient.phoneNumber}
              </span>

            </div>

            <CheckCircle
              size={22}
              className="verified-icon"
            />

          </div>
        )}

        {/* ==================================================
            AMOUNT
        ================================================== */}

        <div className="form-section">

          <label>
            Amount
          </label>

          <div className="amount-input">

            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {

                const value =
                  e.target.value;

                /*
                 * Allow only numbers and decimal.
                 * Maximum two decimal places.
                 */

                if (
                  value === "" ||
                  /^\d*(\.\d{0,2})?$/.test(
                    value
                  )
                ) {
                  setAmount(value);
                  setError("");
                }

              }}
              placeholder="0.00"
              disabled={sending}
            />

            <span>PKR</span>

          </div>

        </div>

        {/* ==================================================
            REVIEW BUTTON
        ================================================== */}

        <button
          className="primary-button review-button"
          onClick={handleReview}
          disabled={
            loadingBalance ||
            !senderPhone ||
            !recipient ||
            !amount
          }
          type="button"
        >

          Review Transfer

          <ArrowRight size={19} />

        </button>

      </div>
    </div>
  );
};

export default SendBaitCash;